/**
 * The Workflows list as data: its filters (folder, owner, health, trigger,
 * on or off, tag, a name search, archived shown or not), grouping by
 * folder, the tidy-up count, and the name suggested for a new workflow.
 * Pure, so it is tested without a screen.
 */
import type { Vocab } from '../../vocab';
import type {
  TriggerKind, WorkflowHealthState, WorkflowStep, WorkflowSummary, WorkflowTrigger,
} from '../../api/workflows';
import { cfg, linkLabel, str } from './workflowModel';

/** `''` (or absent) means "any". `owner` is a user id, `me`, or `none`. `folder` is a name, or `none`. */
export type ListFilters = {
  q: string;
  folder: string;
  owner: string;
  health: WorkflowHealthState | '';
  trigger: TriggerKind | '';
  enabled: 'on' | 'off' | '';
  tag: string;
  archived: boolean;
};

export const NO_FILTERS: ListFilters = {
  q: '', folder: '', owner: '', health: '', trigger: '', enabled: '', tag: '', archived: false,
};

const KEYS = ['q', 'folder', 'owner', 'health', 'trigger', 'enabled', 'tag'] as const;

/** Filters from the address (`?health=never_runs&owner=me`, as the monthly nudge links). */
export function filtersFrom(params: URLSearchParams): ListFilters {
  const f: ListFilters = { ...NO_FILTERS };
  for (const k of KEYS) (f as Record<string, unknown>)[k] = params.get(k) ?? '';
  f.archived = params.get('archived') === '1' || f.health === 'archived';
  return f;
}

/** Filters into the address, leaving other parameters alone. */
export function filtersInto(params: URLSearchParams, f: ListFilters): URLSearchParams {
  const next = new URLSearchParams(params);
  for (const k of KEYS) {
    const v = f[k];
    if (v) next.set(k, String(v));
    else next.delete(k);
  }
  if (f.archived) next.set('archived', '1');
  else next.delete('archived');
  return next;
}

export function activeFilterCount(f: ListFilters): number {
  return KEYS.filter((k) => k !== 'q' && !!f[k]).length + (f.archived ? 1 : 0);
}

export function filterWorkflows(list: WorkflowSummary[], f: ListFilters, me: string | null): WorkflowSummary[] {
  const q = f.q.trim().toLowerCase();
  return list.filter((w) => {
    if (w.archived && !f.archived) return false;
    if (q && !`${w.name} ${w.description} ${w.trigger_text} ${w.tags.join(' ')}`.toLowerCase().includes(q)) return false;
    if (f.folder === 'none' ? w.folder !== '' : f.folder && w.folder.toLowerCase() !== f.folder.toLowerCase()) return false;
    if (f.owner) {
      const want = f.owner === 'me' ? me : f.owner === 'none' ? null : f.owner;
      if ((w.owner_user_id ?? null) !== want) return false;
    }
    if (f.health && w.health.state !== f.health) return false;
    if (f.trigger && w.trigger.kind !== f.trigger) return false;
    if (f.enabled === 'on' && !w.enabled) return false;
    if (f.enabled === 'off' && w.enabled) return false;
    if (f.tag && !w.tags.includes(f.tag)) return false;
    return true;
  });
}

export type FolderGroup = { folder: string; workflows: WorkflowSummary[] };

/**
 * By folder, folders in name order and "No folder" last. Folders with
 * nothing in them (after filtering) are left out unless `all` names them.
 */
export function groupByFolder(list: WorkflowSummary[], all: string[] = []): FolderGroup[] {
  const by = new Map<string, WorkflowSummary[]>();
  for (const name of all) by.set(name, []);
  for (const w of list) {
    const key = [...by.keys()].find((k) => k.toLowerCase() === w.folder.toLowerCase()) ?? w.folder;
    by.set(key, [...(by.get(key) ?? []), w]);
  }
  const named = [...by.entries()]
    .filter(([k, ws]) => k !== '' && ws.length > 0)
    .sort(([a], [b]) => a.localeCompare(b, 'en-GB', { sensitivity: 'base' }))
    .map(([folder, workflows]) => ({ folder, workflows }));
  const none = by.get('') ?? [];
  return none.length ? [...named, { folder: '', workflows: none }] : named;
}

/** Switched-on, unarchived workflows that have not run in 30 days: the tidy-up banner's count. */
export function quietCount(list: WorkflowSummary[]): number {
  return list.filter((w) => !w.archived && w.health.state === 'never_runs').length;
}

/** Every tag in use, in order. */
export function allTags(list: WorkflowSummary[]): string[] {
  return [...new Set(list.flatMap((w) => w.tags))].sort();
}

const lower = (s: string) => (s ? s[0]!.toLowerCase() + s.slice(1) : s);

/** The "when" half of a suggested name: "Energy Premium lead", "Deal won". */
export function triggerPhrase(v: Vocab, t: WorkflowTrigger, names: (id: string) => string | undefined): string {
  switch (t.kind) {
    case 'submission_arrived': {
      const ids = t.campaign_ids ?? [];
      if (!ids.length) return `New ${v.one('lead')}`;
      const first = names(ids[0]!) ?? ids[0]!;
      return `${first}${ids.length > 1 ? ` +${ids.length - 1}` : ''} ${v.one('lead')}`;
    }
    case 'person_property_changed': return t.property_key ? `${t.property_key} changes` : `${v.One('property')} changes`;
    case 'person_owner_changed': return `New ${v.one('owner')}`;
    case 'score_band_changed': return t.band ? `Becomes ${t.band}` : 'Score band changes';
    case 'deal_created': return `New ${v.one('deal')}`;
    case 'deal_stage_changed': return t.to_stage_id ? `${v.One('deal')} to ${names(t.to_stage_id) ?? v.one('stage')}` : `${v.One('deal')} moves`;
    case 'deal_won': return `${v.One('deal')} won`;
    case 'deal_lost': return `${v.One('deal')} lost`;
    case 'task_overdue': return `Overdue ${v.one('task')}`;
    case 'email_event': return 'Email event';
    case 'email_link_clicked': return t.link_name ? `Clicked '${linkLabel(t.link_name)}'` : 'Link clicked';
    case 'enrolment': return `Enrolled ${t.record_type === 'submission' ? v.many('lead') : v.many('person')}`;
    case 'form_submitted': return 'Form submitted';
    case 'lead_returned': {
      const ids = t.campaign_ids ?? [];
      if (!ids.length) return `${v.One('lead')} returned`;
      return `${names(ids[0]!) ?? ids[0]!}${ids.length > 1 ? ` +${ids.length - 1}` : ''} ${v.one('lead')} returned`;
    }
  }
}

/** The "then" half: what the first step that is not a wait does. */
export function actionPhrase(v: Vocab, steps: WorkflowStep[], names: (id: string) => string | undefined): string | null {
  const s = steps.find((x) => x.kind !== 'wait' && x.kind !== 'wait_until');
  if (!s) return null;
  const c = cfg(s);
  switch (s.kind) {
    case 'send_email': {
      const t = names(str(c.template_id));
      return t ? lower(t) : 'email';
    }
    case 'assign_owner': return str(c.mode) === 'user' ? `assign to ${names(str(c.user_id)) ?? 'someone'}` : 'assign through rotation';
    case 'create_task': return str(c.subject) ? `task: ${str(c.subject).replace(/\{\{\w+\}\}/g, '').trim()}` : v.one('task');
    case 'notify': return 'notify';
    case 'set_property': return str(c.key) ? `set ${str(c.key)}` : `set ${v.one('property')}`;
    case 'move_stage': return `move to ${names(str(c.stage_id)) ?? v.one('stage')}`;
    case 'webhook': return 'webhook';
    case 'branch': return 'if / else';
    case 'send_sms': return 'SMS';
    default: return null;
  }
}

/**
 * A name for a new workflow from its trigger and first step, "Energy
 * Premium lead → broadband welcome", at most 100 characters. Editable: it
 * only fills the name while nobody has typed one.
 */
export function suggestName(
  v: Vocab, t: WorkflowTrigger, steps: WorkflowStep[], names: (id: string) => string | undefined,
): string {
  const when = triggerPhrase(v, t, names);
  const then = actionPhrase(v, steps, names);
  return (then ? `${when} → ${then}` : when).slice(0, 100);
}
